<!--
SearchAutocomplete Component - Server-side search autocomplete

USE CASES:
==========
- Client search by name (filtered by franchise)
- Any entity search where options come from a backend endpoint
- Supports dynamic query params that change based on form state

PROPS:
======
- modelValue: Selected ID (v-model)
- endpoint: API endpoint path (e.g., '/index.php/clientes/busca_por_nome')
- queryParam: Query string param name for the search term (default: 'q')
- params: Additional query params object (e.g., { franchise_id: 14 })
- optionLabel: Field name to display in dropdown (default: 'Name')
- optionValue: Field name to use as value (default: 'ID')
- displayFormat: Optional function(item) => string for custom display (overrides optionLabel)
- label: Label text
- placeholder: Placeholder text
- elemId: Unique element ID
- disabled: Disabled state
- required: Shows required indicators
- validation: Validation function
- minLength: Min chars before searching (default: 1)

EVENTS:
=======
- update:modelValue: Emitted with selected ID
- item-select: Emitted with the full selected item object
-->

<template>
  <div class="select-list-wrapper">
    <!-- Label with validation indicators -->
    <label
      v-if="label && label !== ''"
      :for="elemId"
      class="select-list-label"
      :class="{
        'text-warning': !hasValidation && isRequired,
        'text-success': hasValidation && isRequired
      }"
    >
      <i v-if="isRequired && !hasValidation" class="pi pi-exclamation-triangle text-warning"></i>
      <i v-if="isRequired && hasValidation" class="pi pi-check text-success"></i>
      <span v-if="isRequired" class="required-indicator">*</span>
      {{ label }}
    </label>

    <!-- PrimeVue AutoComplete with server-side search -->
    <div class="autocomplete-container">
      <AutoComplete
        :inputId="elemId"
        v-model="selectedItem"
        :suggestions="suggestions"
        @complete="onComplete"
        @change="onChange"
        @item-select="onItemSelect"
        optionLabel="_displayLabel"
        dropdown
        :disabled="isDisabled"
        :placeholder="placeholder || ''"
        :invalid="!hasValidation && isRequired"
        :loading="isSearching"
        forceSelection
        fluid
        appendTo="body"
      />
      <i v-if="isSearching" class="pi pi-spin pi-spinner autocomplete-spinner"></i>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import AutoComplete from 'primevue/autocomplete';
import { httpService, type ApiResponse } from '../services/httpService';
import { log, error as logError } from '../utils/DebugLogger';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null
  },
  endpoint: {
    type: String,
    required: true
  },
  queryParam: {
    type: String,
    default: 'q'
  },
  params: {
    type: Object as import('vue').PropType<Record<string, string>>,
    default: () => ({})
  },
  optionLabel: {
    type: String,
    default: 'Name'
  },
  optionValue: {
    type: String,
    default: 'ID'
  },
  displayFormat: {
    type: Function as unknown as import('vue').PropType<((item: any) => string) | null>,
    default: null
  },
  label: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  elemId: {
    type: String,
    default: 'search-autocomplete'
  },
  disabled: {
    type: [String, Boolean],
    default: false
  },
  required: {
    type: [String, Boolean],
    default: false
  },
  validation: {
    type: Function as import('vue').PropType<() => boolean>,
    default: () => true
  },
  minLength: {
    type: Number,
    default: 1
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: string | number | null];
  'item-select': [item: any];
}>();

// Reactive data
const selectedItem = ref<any>(null);
const suggestions = ref<any[]>([]);
const isSearching = ref<boolean>(false);

// Computed
const isRequired = computed((): boolean =>
  props.required === true || props.required === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  props.validation()
);

// Methods
const onComplete = async (event: { query: string }): Promise<void> => {
  const query = event.query || '';

  if (query.length < props.minLength && query.length > 0) {
    suggestions.value = [];
    return;
  }

  isSearching.value = true;

  try {
    const queryParams = new URLSearchParams({
      [props.queryParam]: query,
      ...props.params
    });

    const url = `${props.endpoint}?${queryParams.toString()}`;
    log('SearchAutocomplete', 'onComplete', 'Fetching', { url });

    const response = await httpService.get<ApiResponse<unknown[]>>(url);

    if (response.success && Array.isArray(response.dados)) {
      // Map items and build display label
      suggestions.value = response.dados.map((item: any) => {
        const id = item[props.optionValue] || item.ID || item.id;
        const label = props.displayFormat
          ? props.displayFormat(item)
          : (item[props.optionLabel] || item.Name || item.name);
        return {
          ...item,
          ID: id,
          Name: label,
          _displayLabel: label
        };
      });
    } else {
      suggestions.value = [];
    }
  } catch (err) {
    logError('SearchAutocomplete', 'onComplete', 'Error fetching suggestions', err);
    suggestions.value = [];
  } finally {
    isSearching.value = false;
  }
};

const onChange = (event: { value: any }): void => {
  const value = event.value;
  log('SearchAutocomplete', 'onChange', 'Value changed', value);

  if (value && typeof value === 'object') {
    const id = value[props.optionValue] || value.ID;
    emit('update:modelValue', id);
  } else if (!value) {
    emit('update:modelValue', null);
  }
};

const onItemSelect = (event: { value: any }): void => {
  const item = event.value;
  log('SearchAutocomplete', 'onItemSelect', 'Item selected', item);
  emit('item-select', item);
};

// Watch modelValue to clear selection when parent resets
watch(() => props.modelValue, (newVal) => {
  if (newVal === null || newVal === undefined || newVal === '') {
    selectedItem.value = null;
  }
});

// Watch params — when they change (e.g., franchise changes), clear selection
watch(() => props.params, () => {
  log('SearchAutocomplete', 'watch:params', 'Params changed, clearing selection');
  selectedItem.value = null;
  suggestions.value = [];
  emit('update:modelValue', null);
}, { deep: true });
</script>

<style scoped>
.autocomplete-container {
  position: relative;
}

.autocomplete-spinner {
  position: absolute;
  right: 40px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--p-text-muted-color);
  font-size: 0.875rem;
}
</style>
